/**
 * Reading-optimized markdown renderer for blog post bodies.
 *
 * Distinct from the in-app MarkdownRenderer because long-form prose wants
 * larger type, generous line-height, and serif headings to match the site's
 * marketing pages — those defaults would be wrong inside goal-context panels.
 */
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const components: Components = {
  h1: (props) => (
    <h1
      {...props}
      className="mb-4 mt-10 font-serif text-3xl font-semibold text-text-primary first:mt-0"
    />
  ),
  h2: (props) => (
    <h2
      {...props}
      className="mb-3 mt-10 font-serif text-2xl font-semibold text-text-primary first:mt-0"
    />
  ),
  h3: (props) => (
    <h3
      {...props}
      className="mb-3 mt-8 font-serif text-xl font-semibold text-text-primary"
    />
  ),
  h4: (props) => (
    <h4 {...props} className="mb-2 mt-6 text-base font-semibold text-text-primary" />
  ),
  p: (props) => (
    <p {...props} className="my-4 text-[17px] leading-[1.7] text-text-primary" />
  ),
  ul: (props) => (
    <ul {...props} className="my-4 list-disc space-y-2 pl-6 text-[17px] leading-[1.7] text-text-primary" />
  ),
  ol: (props) => (
    <ol {...props} className="my-4 list-decimal space-y-2 pl-6 text-[17px] leading-[1.7] text-text-primary" />
  ),
  li: (props) => <li {...props} className="leading-[1.7]" />,
  strong: (props) => <strong {...props} className="font-semibold text-text-primary" />,
  em: (props) => <em {...props} className="italic" />,
  blockquote: (props) => (
    <blockquote
      {...props}
      className="my-5 border-l-4 border-primary/40 bg-surface px-5 py-2 text-[16px] leading-[1.7] text-text-secondary [&>p]:my-2"
    />
  ),
  a: (props) => (
    <a
      {...props}
      className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
    />
  ),
  code: (props) => (
    <code
      {...props}
      className="rounded bg-surface-highlight px-1.5 py-0.5 font-mono text-[0.9em]"
    />
  ),
  hr: () => <hr className="my-8 border-border" />,
};

export const BlogProse = ({ source }: { source: string }) => (
  <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
    {source}
  </ReactMarkdown>
);
